import { FormEvent, useEffect, useId, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import Button from '@components/atoms/ui-kit/button.tsx';
import { useThemeContext } from '@utils/hooks/use-theme-context.ts';
import { useLiveSections } from '@utils/providers/use-live-sections.ts';
import { LiveDraft } from '@utils/editors/live-transcript-state.ts';
import { LiveSectionCreate } from '@utils/types/editors/live-section.ts';
import { LiveRangeInput } from './live-range-input.tsx';
import {
  LiveSectionConfirmation,
  LiveSectionPendingAction
} from './live-section-confirmation.tsx';
import { LiveSectionError } from './live-section-error.tsx';
import { liveSectionHidesDirtyRows } from './live-section-drafts.ts';

type Props = {
  sections: ReturnType<typeof useLiveSections>;
  rows: LiveDraft[];
  canEdit: boolean;
  onDraftExitChange?:
    | ((requestExit: ((after: () => void) => void) | null) => void)
    | undefined;
};
const time = (milliseconds: number) =>
  new Date(milliseconds).toISOString().slice(11, 19);

export const LiveSectionsToolbar = ({
  sections,
  rows,
  canEdit,
  onDraftExitChange
}: Props) => {
  const { t } = useTranslation();
  const helpId = useId();
  const { getColour } = useThemeContext();
  const [draft, setDraft] = useState<LiveSectionCreate | null>(null);
  const [initialDraft, setInitialDraft] = useState<LiveSectionCreate | null>(
    null
  );
  const [pending, setPending] = useState<LiveSectionPendingAction | null>(null);
  const [actionBusy, setActionBusy] = useState(false);
  const rowsRef = useRef(rows);
  rowsRef.current = rows;
  const fieldStyle = {
    backgroundColor: getColour('baseLayer'),
    color: getColour('normalText'),
    borderColor: getColour('border')
  };
  const start = rows.find(
    row => row.segment.segment_id === draft?.start_segment_id
  );
  const end = rows.find(
    row => row.segment.segment_id === draft?.end_segment_id
  );
  const valid =
    draft?.name.trim() &&
    start &&
    end &&
    start.segment.sequence <= end.segment.sequence;
  const draftChanged =
    !!draft &&
    !!initialDraft &&
    (draft.name !== initialDraft.name ||
      draft.start_segment_id !== initialDraft.start_segment_id ||
      draft.end_segment_id !== initialDraft.end_segment_id);
  const clearDraft = () => {
    setDraft(null);
    setInitialDraft(null);
  };
  const requestDraftExit = (after: () => void) => {
    if (sections.busy) return;
    if (draftChanged) setPending({ kind: 'discard', after });
    else {
      clearDraft();
      after();
    }
  };
  useEffect(() => {
    onDraftExitChange?.(draft ? requestDraftExit : null);
  }, [draft, draftChanged, onDraftExitChange, sections.busy]);
  useEffect(() => () => onDraftExitChange?.(null), [onDraftExitChange]);
  const newSection = () => {
    const next = {
      section_id: crypto.randomUUID(),
      name: '',
      start_segment_id: rows[0].segment.segment_id,
      end_segment_id: rows[rows.length - 1].segment.segment_id
    };
    setDraft(next);
    setInitialDraft(next);
  };
  const create = async (event: FormEvent) => {
    event.preventDefault();
    if (draft && valid && (await sections.create(draft))) clearDraft();
  };
  const requestSelection = (target: string) => {
    const change = () => {
      if (!target) {
        void sections.select(target);
        return;
      }
      void sections.select(target, section => {
        if (!liveSectionHidesDirtyRows(section, rowsRef.current)) return true;
        setPending({ kind: 'switch', target });
        return false;
      });
    };
    if (draft) requestDraftExit(change);
    else change();
  };
  const confirmPending = async () => {
    if (!pending || actionBusy || sections.busy) return;
    if (pending.kind === 'discard') {
      const after = pending.after;
      clearDraft();
      setPending(null);
      after();
      return;
    }
    if (pending.kind === 'switch') {
      const target = pending.target;
      setPending(null);
      await sections.select(target);
      return;
    }
    if (sections.selected?.section_id !== pending.sectionId) {
      setPending(null);
      return;
    }
    setActionBusy(true);
    const succeeded = await sections.update(pending.action);
    setActionBusy(false);
    if (succeeded) setPending(null);
  };
  return (
    <section
      aria-label={t('live-sections')}
      className="rounded-xl border p-3 flex flex-col gap-2"
      style={{
        borderColor: getColour('border'),
        backgroundColor: getColour('secondLayer')
      }}
    >
      <h2 className="font-semibold text-sm">{t('live-sections')}</h2>
      <p
        id={helpId}
        className="text-xs"
        style={{ color: getColour('inactiveText') }}
      >
        {t('live-sections-help')}
      </p>
      <div className="flex flex-wrap items-center gap-2">
        <label className="flex items-center gap-2 w-full md:w-auto md:flex-1 min-w-40">
          <span>{t('live-show-transcript')}</span>
          <select
            aria-label={t('live-selected-section')}
            aria-describedby={helpId}
            value={sections.selected?.section_id || ''}
            disabled={sections.busy || actionBusy || !!pending}
            className="rounded-lg border p-2 min-w-0 flex-1"
            style={fieldStyle}
            onChange={event => {
              requestSelection(event.target.value);
            }}
          >
            <option value="">{t('live-full-event')}</option>
            {sections.sections.map(section => (
              <option key={section.section_id} value={section.section_id}>
                {section.name} ·{' '}
                {t(
                  section.completed
                    ? 'live-section-complete'
                    : 'live-section-saved'
                )}
              </option>
            ))}
          </select>
        </label>
        <Button
          size="small"
          variant="outline"
          label={t('live-new-section')}
          disabled={
            !canEdit || !rows.length || sections.busy || !!draft || !!pending
          }
          onClick={newSection}
        />
        {sections.selected && (
          <Button
            size="small"
            variant="outline"
            label={t(
              sections.selected.completed
                ? 'live-reopen-section'
                : 'live-complete-section'
            )}
            disabled={!canEdit || sections.busy || !!draft || !!pending}
            onClick={() => {
              if (!sections.selected) return;
              setPending({
                kind: 'section-action',
                action: sections.selected.completed ? 'reopen' : 'complete',
                sectionId: sections.selected.section_id
              });
            }}
          />
        )}
      </div>
      {sections.selected && (
        <p className="text-sm" style={{ color: getColour('inactiveText') }}>
          {time(sections.selected.start_ms)}–{time(sections.selected.end_ms)} ·{' '}
          {t(
            sections.selected.completed
              ? 'live-section-locked'
              : 'live-section-save-hint'
          )}
        </p>
      )}
      {draft && (
        <form
          onSubmit={event => {
            void create(event);
          }}
          className="grid grid-cols-2 gap-2"
        >
          <label className="col-span-2 flex flex-col gap-1 text-sm">
            {t('live-section-name')}
            <input
              maxLength={160}
              value={draft.name}
              disabled={sections.busy || !canEdit}
              onChange={event =>
                setDraft({ ...draft, name: event.target.value })
              }
              className="rounded-lg border p-2"
              style={fieldStyle}
              autoFocus
            />
          </label>
          {(['start_segment_id', 'end_segment_id'] as const).map(boundary => (
            <LiveRangeInput
              key={boundary}
              label={t(
                boundary === 'start_segment_id'
                  ? 'live-section-start'
                  : 'live-section-end'
              )}
              rows={rows}
              selectedId={draft[boundary]}
              disabled={sections.busy || !canEdit}
              style={fieldStyle}
              select={id => setDraft({ ...draft, [boundary]: id })}
            />
          ))}
          <div className="col-span-2 flex gap-2">
            <Button
              type="submit"
              size="small"
              label={t('live-save-section')}
              disabled={!valid || sections.busy || !canEdit}
            />
            <Button
              size="small"
              variant="outline"
              label={t('cancel')}
              disabled={sections.busy}
              onClick={() => requestDraftExit(() => {})}
            />
          </div>
        </form>
      )}
      {sections.error && (
        <LiveSectionError
          error={sections.error}
          busy={sections.busy}
          onReload={() => void sections.reload()}
        />
      )}
      <LiveSectionConfirmation
        pending={pending}
        sectionName={sections.selected?.name}
        busy={actionBusy || sections.busy}
        error={sections.error}
        onClose={() => !actionBusy && setPending(null)}
        onConfirm={confirmPending}
      />
    </section>
  );
};
